import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users as UsersIcon, Settings as SettingsIcon, Eye, Edit, Download, Loader2, Trash2, UserPlus } from 'lucide-react';
import { FaMicrosoft } from 'react-icons/fa';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  admin: 'Administrateur',
  editor: 'Modificateur',
  reader: 'Lecteur'
};

const ROLE_COLORS = {
  admin: 'bg-red-500',
  editor: 'bg-blue-500',
  reader: 'bg-gray-500'
};

export default function Users() {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    syncAzureUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: getAuthHeader()
      });
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Accès refusé - vous devez être administrateur');
      } else {
        toast.error('Erreur lors du chargement des utilisateurs');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      // Fetch Azure DevOps projects instead of local projects
      const response = await axios.get(`${API_URL}/api/azure-devops/projects`, {
        headers: getAuthHeader()
      });

      // Transform Azure projects to the format we need
      const azureProjects = response.data.projects || [];
      const transformedProjects = azureProjects.map(proj => ({
        id: proj.id, // Azure project ID
        name: proj.name,
        description: proj.description || `Projet Azure DevOps - ${proj.state}`,
        azure_devops_id: proj.id,
        organization: response.data.organization
      }));

      setProjects(transformedProjects);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Azure DevOps non configuré - allez dans Paramètres');
      }
    }
  };

  const syncAzureUsers = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/users/import-azure`,
        {},
        { headers: getAuthHeader() }
      );
      if (response.data.created > 0 || response.data.removed > 0) {
        toast.info(response.data.message);
        fetchUsers();
      }
    } catch {
      // Silently fail on auto-sync (user can manually retry)
    }
  };

  const handleImportUsers = async () => {
    setImporting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/users/import-azure`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'import des utilisateurs';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(
        `${API_URL}/api/users/${userId}`,
        { role: newRole },
        { headers: getAuthHeader() }
      );
      toast.success('Rôle mis à jour');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du rôle');
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      toast.error('L\'email est requis');
      return;
    }
    setAddingUser(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/users/add-to-azure`,
        { email: newUserEmail.trim(), name: newUserName.trim() || null },
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      setShowAddDialog(false);
      setNewUserEmail('');
      setNewUserName('');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'ajout';
      toast.error(msg);
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      const response = await axios.delete(
        `${API_URL}/api/users/${userToDelete.id}`,
        { headers: getAuthHeader() }
      );
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(msg);
    }
  };

  const handleOpenProjectDialog = async (user) => {
    setSelectedUser(user);
    try {
      const response = await axios.get(
        `${API_URL}/api/users/${user.id}/projects`,
        { headers: getAuthHeader() }
      );
      // Map local projects to Azure project IDs
      const assignedAzureIds = response.data
        .filter(p => p.azure_devops_id)
        .map(p => p.azure_devops_id);
      setSelectedProjects(assignedAzureIds);
      setShowProjectDialog(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des projets');
    }
  };

  const handleSaveProjects = async () => {
    try {
      // Prepare Azure project metadata
      const azureProjectsData = selectedProjects.map(projId => {
        const project = projects.find(p => p.id === projId);
        return {
          name: project?.name,
          description: project?.description,
          organization: project?.organization
        };
      });

      await axios.post(
        `${API_URL}/api/users/${selectedUser.id}/projects`,
        {
          project_ids: selectedProjects,
          azure_projects: azureProjectsData
        },
        { headers: getAuthHeader() }
      );
      toast.success('Projets Azure assignés avec succès');
      setShowProjectDialog(false);
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de l\'assignation des projets');
    }
  };

  const toggleProject = (projectId) => {
    setSelectedProjects(prev =>
      prev.includes(projectId)
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-3xl font-heading font-extrabold">Utilisateurs</h1>
          <p className="text-muted-foreground mt-1">
            Gestion des utilisateurs, roles et acces aux projets
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            className="rounded-sm"
            size="sm"
            onClick={() => setShowAddDialog(true)}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Ajouter un utilisateur Azure DevOps</span>
            <span className="sm:hidden">Ajouter</span>
          </Button>
          <Button
            variant="outline"
            className="rounded-sm"
            size="sm"
            onClick={handleImportUsers}
            disabled={importing}
          >
            {importing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">Importer depuis Azure DevOps</span>
            <span className="sm:hidden">Importer</span>
          </Button>
          <Badge variant="secondary" className="gap-1.5">
            <UsersIcon className="h-3.5 w-3.5" />
            {users.length}
          </Badge>
        </div>
      </div>

      {/* Users list */}
      <Card>
        <CardHeader className="px-4 sm:px-6">
          <CardTitle>Utilisateurs de l'organisation</CardTitle>
          <CardDescription>
            Cliquez sur "Importer" pour synchroniser les membres de votre organisation Azure DevOps.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <div className="space-y-3">
            {users.map((user) => (
              <div
                key={user.id}
                className="p-3 sm:p-4 border rounded-sm hover:bg-muted/50 transition-colors"
              >
                {/* Top row: avatar + name + actions */}
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <UsersIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm sm:text-base truncate">{user.name}</div>
                    <div className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1.5 truncate">
                      <FaMicrosoft className="h-3 w-3 text-blue-500 flex-shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  </div>
                  {/* Desktop actions inline */}
                  <div className="hidden lg:flex items-center gap-3">
                    <Badge variant="secondary" className="gap-1">
                      <SettingsIcon className="h-3 w-3" />
                      {user.projects_count} projet{user.projects_count > 1 ? 's' : ''}
                    </Badge>
                    <Select
                      value={user.role}
                      onValueChange={(value) => handleRoleChange(user.id, value)}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            Administrateur
                          </div>
                        </SelectItem>
                        <SelectItem value="editor">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            Modificateur
                          </div>
                        </SelectItem>
                        <SelectItem value="reader">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                            Lecteur
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenProjectDialog(user)}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Gérer les projets
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => { setUserToDelete(user); setShowDeleteDialog(true); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Mobile actions */}
                <div className="flex flex-col gap-2 mt-3 lg:hidden">
                  {/* Ligne 1 : nombre de projets */}
                  <div>
                    <Badge variant="secondary" className="gap-1 text-xs">
                      <SettingsIcon className="h-3 w-3" />
                      {user.projects_count} projet{user.projects_count > 1 ? 's' : ''}
                    </Badge>
                  </div>
                  {/* Ligne 2 : rôle + gérer projets + supprimer */}
                  <div className="flex items-center gap-2">
                    <Select
                      value={user.role}
                      onValueChange={(value) => handleRoleChange(user.id, value)}
                    >
                      <SelectTrigger className="w-[130px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-red-500"></div>
                            Administrateur
                          </div>
                        </SelectItem>
                        <SelectItem value="editor">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                            Modificateur
                          </div>
                        </SelectItem>
                        <SelectItem value="reader">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                            Lecteur
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => handleOpenProjectDialog(user)}
                    >
                      <Edit className="h-3.5 w-3.5 mr-1" />
                      Projets
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => { setUserToDelete(user); setShowDeleteDialog(true); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {users.length === 0 && (
              <div className="text-center py-12">
                <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground font-medium">Aucun utilisateur trouvé</p>
                <p className="text-sm text-muted-foreground/70 mt-2">
                  Cliquez sur "Importer" pour récupérer les membres de votre organisation
                </p>
                <Button
                  className="mt-4 rounded-sm"
                  onClick={handleImportUsers}
                  disabled={importing}
                >
                  {importing ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Importer depuis Azure DevOps
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Ajouter un utilisateur à Azure DevOps
            </DialogTitle>
            <DialogDescription>
              L'utilisateur sera ajouté à votre organisation Azure DevOps et à cette application.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="new-user-email">Email (requis)</Label>
              <Input
                id="new-user-email"
                type="email"
                placeholder="utilisateur@entreprise.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-user-name">Nom (optionnel)</Label>
              <Input
                id="new-user-name"
                placeholder="Prénom Nom"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddUser} disabled={addingUser}>
              {addingUser ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Ajouter
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Supprimer l'utilisateur
            </DialogTitle>
            <DialogDescription>
              Voulez-vous vraiment supprimer <strong>{userToDelete?.name}</strong> ({userToDelete?.email}) ?
              L'utilisateur sera supprimé de cette application et de votre organisation Azure DevOps.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser}>
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FaMicrosoft className="h-5 w-5 text-blue-500" />
              Projets Azure DevOps - {selectedUser?.name}
            </DialogTitle>
            <DialogDescription>
              Sélectionnez les projets Azure DevOps auxquels cet utilisateur aura accès dans l'application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {projects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Aucun projet Azure DevOps trouvé</p>
                <p className="text-xs mt-2">Configurez Azure DevOps dans les paramètres</p>
              </div>
            ) : (
              projects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center space-x-2 p-3 border rounded-sm hover:bg-muted/50"
                >
                  <Checkbox
                    id={project.id}
                    checked={selectedProjects.includes(project.id)}
                    onCheckedChange={() => toggleProject(project.id)}
                  />
                  <label
                    htmlFor={project.id}
                    className="flex-1 cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    <div className="flex items-center gap-2">
                      {project.name}
                      <Badge variant="outline" className="text-xs">Azure</Badge>
                    </div>
                    {project.description && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {project.description}
                      </div>
                    )}
                  </label>
                </div>
              ))
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowProjectDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveProjects}>
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
